import { memo } from "react";
import * as React from "react";
import { Icon as IconType } from "common/react-plucked";

import { Button, Inner, IconWrapper, Image, IconNodeWrapper, WarningWrapper } from "./style";
import { styled } from "sharedUI/styles";
import { defined } from "common/Support";
import { Tooltip } from "../../../../../ui/smart/tooltip/tooltip";
import { IconBadge } from "../app-button-styles";
import { getNamedIcon } from "common/named-icons";

interface PropsBase {
    href?: string;
    onClick?: () => void;
    isActive?: boolean;
    isSelected?: boolean;
    isEnabled?: boolean;
    background?: string;
    tooltipContent: React.ReactNode;
    extendBackground?: boolean;
    error?: boolean;
}

interface IconProps extends PropsBase {
    icon: IconType;
    isProfileIcon?: boolean;
    isSample?: boolean;
    spin?: boolean;
}

interface ImageProps extends PropsBase {
    imageURL?: string;
}

interface NodeProps extends PropsBase {
    iconNode: React.ReactNode;
    badge?: string;
}

interface ExpandingButtonProps extends PropsBase {
    content: React.ReactNode;
}

const HoverWrappper = styled.div<{ background?: boolean }>`
    :hover {
        #trigger {
            opacity: 1;
        }
    }

    ${p =>
        p.background &&
        `
        background-color: ${p.theme.acceptColor};
        border-radius: 12px;

        transition: filter ${p.theme.hoverTransition};

        :hover {
            filter: brightness(0.8);
        }
    `}
`;

interface ExpandingButtonState {}

class ExpandingButton extends React.PureComponent<ExpandingButtonProps, ExpandingButtonState> {
    private wrapperRef = React.createRef<HTMLDivElement>();

    public render() {
        const { href, onClick, content, error, tooltipContent } = this.props;

        let { isSelected, isActive, isEnabled } = this.props;

        if (isSelected === undefined) {
            isSelected = false;
        }

        if (isActive === undefined) {
            isActive = false;
        }

        if (isEnabled === undefined) {
            isEnabled = true;
        }

        const hrefProps =
            href !== undefined ? { href, as: "a" as const, target: "_blank", rel: "noopener noreferrer" } : {};

        const WarningIcon = getNamedIcon("warning");

        return (
            <HoverWrappper background={this.props.extendBackground} ref={this.wrapperRef}>
                <Button
                    isClickable={onClick !== undefined}
                    onClick={() => {
                        if (onClick !== undefined && isEnabled) {
                            onClick();
                        }
                    }}
                    isEnabled={defined(isEnabled)}
                    isActive={defined(isSelected) || defined(isActive)}
                    isSelected={defined(isSelected)}
                    {...hrefProps}>
                    <Inner>
                        {content}
                        {error && (
                            <WarningWrapper>
                                <WarningIcon />
                            </WarningWrapper>
                        )}
                    </Inner>

                    <Tooltip show={true} target={this.wrapperRef}>
                        {tooltipContent}
                    </Tooltip>
                </Button>
            </HoverWrappper>
        );
    }
}

function IconPanelButtonInner(props: IconProps): JSX.Element {
    const { isSelected, icon: Icon, extendBackground, isProfileIcon, spin } = props;
    const content = (
        <IconWrapper
            marginRight={extendBackground === true ? 0 : 16}
            isProfileIcon={isProfileIcon}
            isSample={true}
            spin={spin}
            isSelected={isSelected}>
            <Icon size={24} />
        </IconWrapper>
    );
    return <ExpandingButton {...props} content={content} />;
}
export const IconPanelButton = memo(IconPanelButtonInner);

export function ImagePanelButton(props: ImageProps): JSX.Element {
    const { imageURL } = props;
    const content = <Image src={imageURL} />;
    return <ExpandingButton {...props} content={content} />;
}

export function NodePanelButton(props: NodeProps): JSX.Element {
    const { iconNode, badge } = props;
    const content = (
        <IconNodeWrapper>
            {iconNode}
            {badge !== undefined && <IconBadge>{badge.toUpperCase()}</IconBadge>}
        </IconNodeWrapper>
    );
    return <ExpandingButton {...props} content={content} />;
}
